import type { ReactNode } from "react";
import { ArrowRight, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import type {
  ConnectivitySection,
  Layout,
  Media,
  PerformanceSection,
  RemoteSection,
  SoftwareSection,
} from "../product-types";
import { ImagePlaceholder, RevealSlider, SectionShell } from "../shared/primitives";
import { reveal } from "../shared/reveal";

const EYEBROW = "text-sm font-medium uppercase tracking-wider text-primary";
const TITLE = "mt-3 text-3xl font-semibold tracking-tight sm:text-4xl";
const BODY = "mt-5 text-base leading-relaxed text-muted-foreground";

const BALANCED_COLS = "lg:grid-cols-2";
const wideCols = (layout: Layout) =>
  layout === "imageLeft" ? "lg:grid-cols-[1.2fr_1fr]" : "lg:grid-cols-[1fr_1.2fr]";

/**
 * Two-column pillar layout. Mobile is always body-first (better reading order);
 * on desktop the media column sits on the side given by `layout`.
 */
function TwoCol({
  layout,
  gridColsClass,
  media,
  body,
}: {
  layout: Layout;
  gridColsClass: string;
  media: ReactNode;
  body: ReactNode;
}) {
  return (
    <div
      className={cn(
        "mx-auto grid max-w-7xl grid-cols-1 gap-12 px-6 py-24 lg:items-center",
        gridColsClass,
      )}
    >
      <div
        className={cn(
          "relative order-2 animate-fade-up",
          layout === "imageLeft" ? "lg:order-1" : "lg:order-2",
        )}
        style={reveal(120)}
      >
        {media}
      </div>
      <div
        className={cn(
          "order-1 animate-fade-up",
          layout === "imageLeft" ? "lg:order-2" : "lg:order-1",
        )}
      >
        {body}
      </div>
    </div>
  );
}

function AuroraBehind({ className }: { className?: string }) {
  if (!className) return null;
  return <div aria-hidden className={className} style={{ background: "var(--gradient-aurora)" }} />;
}

function ProductImage({ media }: { media: Extract<Media, { type: "image" }> }) {
  return (
    <img
      src={media.src}
      alt={media.alt}
      width={media.width}
      height={media.height}
      loading={media.loading}
      className="block h-auto w-full object-cover"
    />
  );
}

export function PerformancePillar({ section }: { section: PerformanceSection }) {
  const { media } = section;
  const mediaNode =
    media.type === "reveal" ? (
      <>
        <AuroraBehind className={media.auroraClassName} />
        <RevealSlider
          opaque={media.opaque}
          transparent={media.transparent}
          width={media.width}
          height={media.height}
          revealLabel={media.label}
          imageAlt={media.alt}
        />
      </>
    ) : media.type === "placeholder" ? (
      <ImagePlaceholder label={media.label} className={media.className ?? "aspect-[3/2]"} />
    ) : null;

  const body = (
    <>
      <p className={EYEBROW}>{section.eyebrow}</p>
      <h2 className={TITLE}>{section.title}</h2>
      <p className={BODY}>{section.body}</p>
      {section.accent.style === "chips" ? (
        <div className="mt-6 flex flex-wrap gap-2">
          {section.accent.value.split(" · ").map((chip) => (
            <span
              key={chip}
              className="rounded-full border border-border/70 bg-card/60 px-3 py-1.5 text-xs text-foreground/90 backdrop-blur"
            >
              {chip}
            </span>
          ))}
        </div>
      ) : (
        <p className="mt-6 inline-block rounded-full border border-border/70 bg-card/60 px-4 py-2 text-xs text-foreground/90 backdrop-blur">
          {section.accent.value}
        </p>
      )}
    </>
  );

  return (
    <SectionShell bgClassName={section.bgClassName}>
      <TwoCol layout={section.layout} gridColsClass={BALANCED_COLS} media={mediaNode} body={body} />
    </SectionShell>
  );
}

export function ConnectivityPillar({ section }: { section: ConnectivitySection }) {
  const { media } = section;
  const mediaNode =
    media.type === "image" ? (
      <div className="relative overflow-hidden rounded-2xl border border-border bg-card">
        <ProductImage media={media} />
      </div>
    ) : media.type === "placeholder" ? (
      <ImagePlaceholder label={media.label} className={media.className ?? "aspect-[3/2]"} />
    ) : null;

  const body = (
    <>
      <p className={EYEBROW}>{section.eyebrow}</p>
      <h2 className={TITLE}>{section.title}</h2>
      {section.caption.style === "list" ? (
        <ul className="mt-6 divide-y divide-border/60">
          {section.caption.value
            .replace(/\.$/, "")
            .split(" · ")
            .map((item) => (
              <li key={item} className="flex items-center gap-3 py-3 text-sm text-foreground/90">
                <ChevronRight className="h-3.5 w-3.5 shrink-0 text-primary" />
                {item}
              </li>
            ))}
        </ul>
      ) : (
        <p className={BODY}>{section.caption.value}</p>
      )}
    </>
  );

  return (
    <SectionShell relative decorations={section.decorations}>
      <TwoCol
        layout={section.layout}
        gridColsClass={wideCols(section.layout)}
        media={mediaNode}
        body={body}
      />
    </SectionShell>
  );
}

export function SoftwarePillar({ section }: { section: SoftwareSection }) {
  const { media } = section;
  const mediaNode =
    media.type === "image" ? (
      <div
        className={cn(
          "relative overflow-hidden rounded-3xl border border-border bg-card",
          media.shadowGlow && "shadow-glow",
        )}
      >
        <AuroraBehind className={media.auroraClassName} />
        <ProductImage media={media} />
      </div>
    ) : media.type === "placeholder" ? (
      <div className="rounded-3xl border border-dashed border-border/50 bg-muted/20">
        <ImagePlaceholder
          label={media.label}
          className={media.className ?? "aspect-[4/3] rounded-3xl"}
        />
      </div>
    ) : null;

  const body = (
    <>
      <p className={EYEBROW}>{section.eyebrow}</p>
      <h2 className={TITLE}>{section.title}</h2>
      <p className={BODY}>{section.body}</p>
      <div className="mt-6 grid gap-3">
        {section.cards.map(([title, cardBody]) => (
          <div
            key={title}
            className="rounded-lg border border-border/70 bg-card/60 px-4 py-3 backdrop-blur"
          >
            <h3 className="text-sm font-medium text-foreground">{title}</h3>
            <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{cardBody}</p>
          </div>
        ))}
      </div>
    </>
  );

  return (
    <SectionShell bgClassName={section.bgClassName}>
      <TwoCol
        layout={section.layout}
        gridColsClass={wideCols(section.layout)}
        media={mediaNode}
        body={body}
      />
    </SectionShell>
  );
}

export function RemotePillar({ section }: { section: RemoteSection }) {
  const { media } = section;
  const mediaNode =
    media.type === "image" ? (
      <>
        <AuroraBehind className={media.auroraClassName} />
        <div
          className={cn(
            "overflow-hidden rounded-2xl border border-border bg-card",
            media.maxWidth && "mx-auto max-w-md",
          )}
        >
          <ProductImage media={media} />
        </div>
      </>
    ) : null;

  const body = (
    <>
      <p className={EYEBROW}>{section.eyebrow}</p>
      <h2 className={TITLE}>{section.title}</h2>
      <p className={BODY}>{section.body}</p>
      <ul className="mt-6 space-y-2 text-sm text-foreground/90">
        {section.bullets.map((line) => (
          <li key={line} className="flex items-start gap-2">
            <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
            <span>{line}</span>
          </li>
        ))}
      </ul>
      <a
        href={section.appLink.href}
        className="mt-6 inline-flex items-center gap-1.5 text-sm font-medium text-primary transition-colors hover:text-primary/80"
      >
        {section.appLink.label}
        <ArrowRight className="h-4 w-4" />
      </a>
      {section.note && (
        <div className="mt-8 rounded-2xl border border-primary/30 bg-primary/10 p-5 backdrop-blur">
          <div className="inline-flex items-center gap-2 rounded-full border border-primary/40 bg-background/60 px-3 py-1 text-xs font-medium text-foreground">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-primary" />
            {section.note.title}
          </div>
          <p className="mt-3 text-sm leading-relaxed text-foreground/90">{section.note.body}</p>
          <a
            href={section.note.link.href}
            className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-primary transition-colors hover:text-primary/80"
          >
            {section.note.link.label}
            <ArrowRight className="h-4 w-4" />
          </a>
        </div>
      )}
    </>
  );

  return (
    <SectionShell relative decorations={section.decorations}>
      <TwoCol
        layout={section.layout}
        gridColsClass={wideCols(section.layout)}
        media={mediaNode}
        body={body}
      />
    </SectionShell>
  );
}
